import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles/Select.module.scss';
import { MeteringPointOption } from '../../types/consumptionRates';
import { OmaLummeApp } from '../../omaLummeContext';

type SelectProps = {
  options: MeteringPointOption[];
  contractGrouping?: boolean;
  idToggle?: boolean;
  search?: string;
  size?: string;
  activeOption: string;
  setActiveOption: (value: React.SetStateAction<string>) => void;
};

const Select: React.FunctionComponent<SelectProps> = (props: SelectProps) => {
  const { t } = useTranslation();

  const [selectOpen, toggleSelectOpen] = React.useState<boolean>(false);
  const [useIds, setUseIds] = React.useState<boolean>(false);
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  const { options, contractGrouping, activeOption, setActiveOption, idToggle, search, size } = props;

  const { basicData } = OmaLummeApp.useState();

  const activeOptionsArray: MeteringPointOption[] = [{ label: t(`billing:invoices.activeInvoices`), value: '', type: 'heading' }];
  const otherOptionsArray: MeteringPointOption[] = [{ label: t(`billing:invoices.otherInvoices`), value: '', type: 'heading' }];
  const inActiveOptionsArray: MeteringPointOption[] = [{ label: t(`billing:invoices.inActiveInvoices`), value: '', type: 'heading' }];
  // These are contract groups other than the normal ones that users may not want to see.
  const otherContractGroupsRet = [10, 22, 23, 24, 25, 31];

  options.map((o) => {
    if (basicData) {
      const optionContractNo = o.value.split('-')[1];
      const optionContractCode = o.value.split('-')[2];

      const basicDataPoint = basicData.find((d) => d.contractNo === optionContractNo && d.contractCode === optionContractCode);

      if (basicDataPoint) {
        if (basicDataPoint.contractStatus.toUpperCase() === 'S') {
          inActiveOptionsArray.push(o);
        } else if (otherContractGroupsRet.includes(parseInt(basicDataPoint.contractTypeNo)) || o.type === 'other') {
          otherOptionsArray.push(o);
        } else {
          activeOptionsArray.push(o);
        }
      }
    }
  });

  // Clear group if there are no contracts in it
  if (activeOptionsArray.length === 1) {
    activeOptionsArray.length = 0;
  }
  if (otherOptionsArray.length === 1) {
    otherOptionsArray.length = 0;
  }
  if (inActiveOptionsArray.length === 1) {
    inActiveOptionsArray.length = 0;
  }

  // First option is "Show all", then add the three groups
  const groupedOptions = [options[0]].concat(activeOptionsArray).concat(otherOptionsArray).concat(inActiveOptionsArray);

  // Remove first option if it isn't "Show all"
  if (groupedOptions[0] && groupedOptions[0].value !== 'all') {
    groupedOptions.shift();
  }

  const escFunction = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        toggleSelectOpen(false);
      }
    },
    [toggleSelectOpen],
  );

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    const isEllipsisActive = (e: HTMLElement | Element) => {
      if (e instanceof HTMLElement) {
        return e.offsetWidth < e.scrollWidth;
      }
    };

    const divs = document.querySelectorAll(`.${styles.option} div`);

    divs.forEach((div) => {
      if (isEllipsisActive(div)) {
        div.classList.add(styles.ellipsis);
      }
    });
  }, [selectOpen, options]);

  function useOutsideAlerter(
    ref: React.RefObject<any>,
    open: {
      selectOpen: boolean;
      toggleSelectOpen: (value: React.SetStateAction<boolean>) => void;
    },
  ) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target)) {
          open.toggleSelectOpen(false);
        }
      }

      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, open]);
  }

  const selectRef = useRef<any>(null);
  useOutsideAlerter(selectRef, { selectOpen, toggleSelectOpen });

  const active = options.find((o) => o.value === activeOption);
  const activeLabel = active
    ? idToggle && useIds && active.type !== 'other'
      ? active?.value.split('-')[0]
      : `${active?.label}${active?.type === 'production' ? ` (${t('consumption:consumption.typeProduction')})` : ''}`
    : contractGrouping
      ? t(`billing:billing.showAll`)
      : undefined;

  return (
    <>
      {activeLabel && activeLabel !== '' && (
        <div ref={selectRef} className={`${styles.select} ${selectOpen ? styles.active : ''} ${size === 'big' ? styles.big : ''}`}>
          <button
            type="button"
            className={`${styles.option} ${selectOpen ? styles.selectOpen : styles.selectClosed}`}
            onClick={(): void => toggleSelectOpen(!selectOpen)}
          >
            <div>
              <span>{activeLabel}</span>
            </div>
          </button>
          {selectOpen && (
            <div className={styles.options}>
              <header>
                {idToggle && (
                  <div className={styles.idCheckbox}>
                    <div className="form-item checkbox">
                      <input type="checkbox" id="useMeteringPointIds" onChange={(): void => setUseIds(!useIds)} checked={useIds} />
                      <div className="form-item-input"></div>
                      <label htmlFor="useMeteringPointIds">{t('consumption:consumption.useMeteringpointIDs')}</label>
                    </div>
                  </div>
                )}
                {search && options.length > 1 && (
                  <div className={styles.search}>
                    <label htmlFor="searchOptions" className="sr-only">
                      {t(`consumption:consumption.searchMeteringPoint`)}
                    </label>
                    <input
                      type="text"
                      id="searchOptions"
                      onChange={(e): void => setSearchQuery(e.currentTarget.value)}
                      value={searchQuery}
                      placeholder={search}
                    />
                    {searchQuery.length > 0 && (
                      <button type="button" onClick={() => setSearchQuery('')}>
                        <span>{t(`consumption:eliq.profileClear`)}</span>
                      </button>
                    )}
                  </div>
                )}
              </header>
              <ul className={styles.optionsList}>
                {(contractGrouping ? groupedOptions : options)
                  .filter((o) => {
                    const notCurrentOption = o.value !== activeOption;
                    let searchMatches = true;
                    if (search !== '' && searchQuery !== '') {
                      searchMatches =
                        o.label.toLowerCase().includes(searchQuery.toLowerCase()) || o.value.includes(searchQuery) || o.type === 'heading';
                    }

                    return contractGrouping ? searchMatches : notCurrentOption && searchMatches;
                  })
                  .map((opt) => {
                    if (opt.type === 'heading') {
                      return (
                        <li key={opt.label} className={styles.heading}>
                          {opt.label}
                        </li>
                      );
                    } else {
                      return (
                        <li key={opt.value}>
                          <button
                            type="button"
                            className={`${styles.option} ${contractGrouping ? styles.grouping : ''} ${
                              opt.value === activeOption ? styles.activeOption : ''
                            }`}
                            key={opt.value}
                            onClick={(): void => {
                              setActiveOption(opt.value);
                              toggleSelectOpen(false);
                              selectRef.current.firstChild.focus();
                            }}
                          >
                            <div className="selectOption">
                              <span>
                                {idToggle && useIds && opt.type !== 'all' && opt.type !== 'other' ? opt.value.split('-')[0] : opt.label}{' '}
                                {opt.type === 'production' ? ` (${t('consumption:consumption.typeProduction')})` : ''}
                              </span>
                            </div>
                          </button>
                        </li>
                      );
                    }
                  })}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Select;
